import $ from 'jquery';
import Swal from 'sweetalert2';
import { getState } from '~src/state';
import { convertToUnit } from '~src/utils/convertToUnit';
import { calculateNewDimensions } from './calculateNewDimensions';
import { applyResizeImage } from './applyResizeImage';

export async function showResizeImageDialog() {
  let activeObject = getState('activeObject');
  if (activeObject.type === 'MagicFrame') {
    activeObject = activeObject.editingImg;
    if (!activeObject) {
      return;
    }
  }
  if (!activeObject || activeObject.type !== 'image') {
    return;
  }

  // Check if image has any modifications that would be lost
  const hasOpacity = parseFloat(activeObject.opacity) < 1;
  const isFlipped = activeObject.flipX || activeObject.flipY;
  const isErased = typeof activeObject.eraser !== 'undefined';
  const hasFilters = activeObject.filters && activeObject.filters.length > 0;

  const hasModifications = hasOpacity || isFlipped || isErased || hasFilters;

  // Get the actual image resolution, not the visual size
  const originalWidth = Math.round(activeObject.width);
  const originalHeight = Math.round(activeObject.height);

  let currentUnit = 'px';
  let scaleProportionally = true;
  let currentQuality = 90;

  // Calculate preset options (75%, 50%, 25% of original)
  const presets = [
    {
      percentage: 75,
      width: Math.round(originalWidth * 0.75),
      height: Math.round(originalHeight * 0.75),
    },
    {
      percentage: 50,
      width: Math.round(originalWidth * 0.5),
      height: Math.round(originalHeight * 0.5),
    },
    {
      percentage: 25,
      width: Math.round(originalWidth * 0.25),
      height: Math.round(originalHeight * 0.25),
    },
  ];

  // Determine which preset is recommended based on original size
  const getRecommendedPreset = () => {
    const totalPixels = originalWidth * originalHeight;

    // If image is already small (< 500K pixels), recommend 25% reduction (75% of original)
    if (totalPixels < 500000) {
      return 0; // 25% reduction
    }
    // If image is medium (500K - 2M pixels), recommend 50% reduction
    else if (totalPixels < 2000000) {
      return 1; // 50% reduction
    }
    // If image is large (> 2M pixels), recommend 75% reduction for very large, 50% for large
    else {
      return totalPixels > 4000000 ? 2 : 1; // 75% reduction for very large, 50% for large
    }
  };

  const recommendedIndex = getRecommendedPreset();

  const getPresetHTML = () => {
    return `
      <div class="resize-image-dialog">
        ${
          hasModifications
            ? `
          <div class="alert alert-warning" role="alert">
            <strong>Attenzione:</strong> L'immagine ha delle modifiche applicate (filtri, opacità, riflessi o cancellazioni) che verranno perse durante il ridimensionamento. Per non perdere le modifiche, puoi prima esportare la foto come allegato dal tasto 'Esporta' in alto a destra.
          </div>
        `
            : ''
        }
        
        <div style="text-align: center; margin-bottom: 20px;">
          <p style="margin-bottom: 10px;">Dimensioni originali: ${originalWidth} × ${originalHeight} px</p>
          <p style="margin-bottom: 5px; font-size: 14px; color: #666;">Seleziona una dimensione predefinita:</p>
        </div>
        
        <div class="preset-buttons" style="display: flex; flex-direction: column; gap: 15px;">
          ${presets
            .map((preset, index) => {
              const sizeReduction = 100 - preset.percentage;
              const isRecommended = index === recommendedIndex;
              return `
              <div style="position: relative;">
                ${
                  isRecommended
                    ? `
                  <div class="recommended-ribbon" style="
                    position: absolute;
                    top: -10px;
                    right: -10px;
                    background: #278e8e;
                    color: white;
                    padding: 4px 16px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: bold;
                    z-index: 1;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                  ">
                    CONSIGLIATO
                  </div>
                `
                    : ''
                }
                <button type="button" class="preset-button swal2-styled ${
                  isRecommended ? 'recommended' : ''
                }" style="
                  width: 100%; 
                  padding: 15px; 
                  font-size: 16px; 
                  background: ${isRecommended ? '#f0f9ff' : '#f0f0f0'}; 
                  color: #333; 
                  border: 1px solid ${isRecommended ? '#278e8e' : '#ddd'};
                  ${isRecommended ? 'border-width: 2px;' : ''}
                " data-width="${preset.width}" data-height="${preset.height}">
                  <strong style="font-size: 18px;">Riduci del ${sizeReduction}%</strong>
                  <br>
                  <small style="font-size: 12px; color: #666;">${
                    preset.width
                  } × ${preset.height} px</small>
                </button>
              </div>
            `;
            })
            .join('')}
        </div>
        
        <div style="margin-top: 20px; text-align: center;">
          <button type="button" id="show-advanced" class="btn btn-success">
            Impostazioni Avanzate
          </button>
        </div>
      </div>
    `;
  };

  const getFormHTML = (width, height, unit, quality) => {
    const widthInUnit =
      unit === 'px' ? width : convertToUnit('px', unit, width);
    const heightInUnit =
      unit === 'px' ? height : convertToUnit('px', unit, height);

    // Format numbers based on unit - no decimals for pixels
    const formatValue = (value, currentUnit) => {
      return currentUnit === 'px' ? Math.round(value) : value.toFixed(2);
    };

    const dimensionReduction = 100 - (width / originalWidth) * 100;
    const showQualityWarning = quality < 70 || dimensionReduction > 80;

    return `
      <div class="resize-image-dialog advanced-mode">
        <div style="text-align: left; margin-bottom: 15px;">
          <button type="button" id="show-presets" class="btn btn-link" style="padding: 0; font-size: 14px;">
            ← Torna alle opzioni rapide
          </button>
        </div>
        
        ${
          hasModifications
            ? `
          <div class="alert alert-warning" role="alert">
            <strong>Attenzione:</strong> L'immagine ha delle modifiche applicate (filtri, opacità, riflessi o cancellazioni) che verranno perse durante il ridimensionamento.
          </div>
        `
            : ''
        }
        <div class="resize-form-group">
          <label class="resize-form-label">Dimensioni</label>
          <div style="display: flex; gap: 10px; align-items: center;">
            <div style="flex: 1;">
              <input type="number" id="resize-width" class="resize-input" value="${formatValue(
                widthInUnit,
                unit
              )}" min="1" step="${
      unit === 'px' ? '1' : '0.01'
    }" pattern="[0-9]*" placeholder="Larghezza">
            </div>
            <div style="color: #666;">×</div>
            <div style="flex: 1;">
              <input type="number" id="resize-height" class="resize-input" value="${formatValue(
                heightInUnit,
                unit
              )}" min="1" step="${
      unit === 'px' ? '1' : '0.01'
    }" pattern="[0-9]*" placeholder="Altezza">
            </div>
            <div style="flex: 0.8;">
              <select id="resize-unit" class="resize-input">
                <option value="px" ${
                  unit === 'px' ? 'selected' : ''
                }>px</option>
                <option value="mm" ${
                  unit === 'mm' ? 'selected' : ''
                }>mm</option>
                <option value="cm" ${
                  unit === 'cm' ? 'selected' : ''
                }>cm</option>
              </select>
            </div>
          </div>
        </div>
        
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 10px;">
          <label class="resize-checkbox-label" style="margin: 0;">
            <input type="checkbox" id="resize-proportional" ${
              scaleProportionally ? 'checked' : ''
            }>
            Ridimensiona proporzionalmente
          </label>
          
          <button type="button" id="resize-restore" class="resize-restore-btn" title="Ripristina dimensioni originali">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
              <path d="M3 3v5h5"></path>
            </svg>
            Ripristina
          </button>
        </div>
        
        <div class="resize-form-group" style="margin-top: 15px;">
          <label class="resize-form-label">Qualità immagine</label>
          <div class="quality-controls mb-4">
            <input type="range" id="resize-quality-slider" class="slider" value="${quality}" min="0" max="100">
            <input type="number" id="resize-quality" class="resize-input quality-number" value="${quality}" min="0" max="100">
          </div>
        </div>
        
        
        <div class="alert alert-danger size-exceeded-warning" role="alert" style="display: none;">
          <strong>Attenzione:</strong> Le dimensioni non possono superare quelle originali (${originalWidth}x${originalHeight}px).
        </div>
        
        ${
          showQualityWarning
            ? `
          <div class="alert alert-warning quality-warning" role="alert">
            <strong>Attenzione:</strong> La qualità dell'immagine potrebbe risultare compromessa con queste impostazioni.
          </div>
        `
            : ''
        }
      </div>
    `;
  };

  let currentWidth = originalWidth;
  let currentHeight = originalHeight;

  const result = await Swal.fire({
    title: 'Riduci dimensioni',
    html: getPresetHTML(),
    showCancelButton: true,
    confirmButtonText: 'Riduci',
    cancelButtonText: 'Annulla',
    showConfirmButton: false,
    width: '500px',
    confirmButtonColor: '#278e8e',
    didOpen: () => {
      let switchToAdvanced;
      let switchToPresets;

      // Setup handlers for preset mode
      const setupPresetHandlers = () => {
        // Handle preset button clicks
        $('.preset-button')
          .off('click')
          .on('click', async function () {
            const width = parseInt($(this).data('width'));
            const height = parseInt($(this).data('height'));

            Swal.close();
            await applyResizeImage(activeObject, width, height, 90);
          });

        // Handle advanced button click
        $('#show-advanced').off('click').on('click', switchToAdvanced);
      };

      // Setup handlers for advanced mode
      const setupAdvancedHandlers = () => {
        // Handle back button
        $('#show-presets').off('click').on('click', switchToPresets);

        // Function to check if values are valid and update button state
        const updateButtonState = () => {
          const widthValid = currentWidth > 0 && currentWidth <= originalWidth;
          const heightValid =
            currentHeight > 0 && currentHeight <= originalHeight;
          const sizeChanged =
            currentWidth !== originalWidth ||
            currentHeight !== originalHeight ||
            currentQuality !== 100;

          const isValid = widthValid && heightValid && sizeChanged;

          // Update confirm button state
          const confirmBtn = Swal.getConfirmButton();
          if (confirmBtn) {
            confirmBtn.disabled = !isValid;
            if (!isValid) {
              confirmBtn.setAttribute(
                'title',
                !sizeChanged
                  ? 'Le dimensioni non sono cambiate'
                  : 'Le dimensioni superano quelle originali'
              );
            } else {
              confirmBtn.removeAttribute('title');
            }
          }

          // Add error styling to inputs if invalid
          $('#resize-width').toggleClass('error', currentWidth > originalWidth);
          $('#resize-height').toggleClass(
            'error',
            currentHeight > originalHeight
          );

          // Show/hide size exceeded warning
          const sizeExceeded =
            currentWidth > originalWidth || currentHeight > originalHeight;
          $('.size-exceeded-warning').toggle(sizeExceeded);
        };

        const updateQualityWarning = () => {
          const quality = parseInt($('#resize-quality').val()) || 90;
          const dimensionReduction = 100 - (currentWidth / originalWidth) * 100;
          const showQualityWarning = quality < 70 || dimensionReduction > 80;

          // Remove any existing quality warning first
          $('.quality-warning').remove();

          if (showQualityWarning) {
            $('.quality-controls').after(`
            <div class="alert alert-warning quality-warning" role="alert">
              <strong>Attenzione:</strong> La qualità dell'immagine potrebbe risultare compromessa con queste impostazioni.
            </div>
          `);
          }
        };

        // Unit change handler
        $('#resize-unit')
          .off('change')
          .on('change', function () {
            currentUnit = $(this).val();

            // Convert and update width
            const widthInUnit =
              currentUnit === 'px'
                ? currentWidth
                : convertToUnit('px', currentUnit, currentWidth);
            const formattedWidth =
              currentUnit === 'px'
                ? Math.round(widthInUnit)
                : widthInUnit.toFixed(2);
            $('#resize-width').val(formattedWidth);

            // Convert and update height
            const heightInUnit =
              currentUnit === 'px'
                ? currentHeight
                : convertToUnit('px', currentUnit, currentHeight);
            const formattedHeight =
              currentUnit === 'px'
                ? Math.round(heightInUnit)
                : heightInUnit.toFixed(2);
            $('#resize-height').val(formattedHeight);

            // Update step attribute based on unit
            const step = currentUnit === 'px' ? '1' : '0.01';
            $('#resize-width').attr('step', step);
            $('#resize-height').attr('step', step);

            // No need to update max values since we handle validation differently

            updateButtonState();
            updateQualityWarning();
          });

        // Prevent non-numeric input
        $('#resize-width, #resize-height')
          .off('keypress')
          .on('keypress', function (e) {
            // Allow: backspace, delete, tab, escape, enter, decimal point
            const allowedKeys = [8, 9, 27, 13, 46, 110, 190];
            // Allow decimal point only for non-pixel units
            if ((e.which === 46 || e.which === 190) && currentUnit === 'px') {
              e.preventDefault();
              return false;
            }
            // Ensure that it is a number and stop the keypress
            if (
              (e.which < 48 || e.which > 57) &&
              !allowedKeys.includes(e.which)
            ) {
              e.preventDefault();
              return false;
            }
          });

        // Width input handler
        $('#resize-width')
          .off('input')
          .on('input', function () {
            const value = $(this).val();
            if (!value || value === '') return;

            const widthInUnit = parseFloat(value);
            if (isNaN(widthInUnit) || widthInUnit <= 0) return;

            const widthInPx =
              currentUnit === 'px'
                ? widthInUnit
                : convertToUnit(currentUnit, 'px', widthInUnit);

            currentWidth = widthInPx;

            if (scaleProportionally) {
              const { height } = calculateNewDimensions(
                originalWidth,
                originalHeight,
                currentWidth,
                null
              );
              currentHeight = height;
              const heightInUnit =
                currentUnit === 'px'
                  ? height
                  : convertToUnit('px', currentUnit, height);
              const formattedHeight =
                currentUnit === 'px'
                  ? Math.round(heightInUnit)
                  : heightInUnit.toFixed(2);
              $('#resize-height').val(formattedHeight);
            }

            updateButtonState();
            updateQualityWarning();
          });

        // Height input handler
        $('#resize-height')
          .off('input')
          .on('input', function () {
            const value = $(this).val();
            if (!value || value === '') return;

            const heightInUnit = parseFloat(value);
            if (isNaN(heightInUnit) || heightInUnit <= 0) return;

            const heightInPx =
              currentUnit === 'px'
                ? heightInUnit
                : convertToUnit(currentUnit, 'px', heightInUnit);

            currentHeight = heightInPx;

            if (scaleProportionally) {
              const { width } = calculateNewDimensions(
                originalWidth,
                originalHeight,
                null,
                currentHeight
              );
              currentWidth = width;
              const widthInUnit =
                currentUnit === 'px'
                  ? width
                  : convertToUnit('px', currentUnit, width);
              const formattedWidth =
                currentUnit === 'px'
                  ? Math.round(widthInUnit)
                  : widthInUnit.toFixed(2);
              $('#resize-width').val(formattedWidth);
            }

            updateButtonState();
            updateQualityWarning();
          });

        // Proportional checkbox handler
        $('#resize-proportional')
          .off('change')
          .on('change', function () {
            scaleProportionally = $(this).is(':checked');

            // If enabling proportional scaling, recalculate height based on current width
            if (scaleProportionally) {
              const { height } = calculateNewDimensions(
                originalWidth,
                originalHeight,
                currentWidth,
                null
              );
              currentHeight = height;
              const heightInUnit =
                currentUnit === 'px'
                  ? height
                  : convertToUnit('px', currentUnit, height);
              const formattedHeight =
                currentUnit === 'px'
                  ? Math.round(heightInUnit)
                  : heightInUnit.toFixed(2);
              $('#resize-height').val(formattedHeight);
              updateButtonState();
              updateQualityWarning();
            }
          });

        // Quality slider handler
        $('#resize-quality-slider')
          .off('input')
          .on('input', function () {
            currentQuality = parseInt($(this).val());
            $('#resize-quality').val(currentQuality);
            updateButtonState();
            updateQualityWarning();
          });

        // Quality number input handler
        $('#resize-quality')
          .off('input')
          .on('input', function () {
            currentQuality = parseInt($(this).val()) || 90;
            $('#resize-quality-slider').val(currentQuality);
            updateButtonState();
            updateQualityWarning();
          });

        // Restore button handler
        $('#resize-restore')
          .off('click')
          .on('click', function () {
            // Reset to original dimensions
            currentWidth = originalWidth;
            currentHeight = originalHeight;
            currentQuality = 90;

            // Update width field
            const widthInUnit =
              currentUnit === 'px'
                ? currentWidth
                : convertToUnit('px', currentUnit, currentWidth);
            const formattedWidth =
              currentUnit === 'px'
                ? Math.round(widthInUnit)
                : widthInUnit.toFixed(2);
            $('#resize-width').val(formattedWidth);

            // Update height field
            const heightInUnit =
              currentUnit === 'px'
                ? currentHeight
                : convertToUnit('px', currentUnit, currentHeight);
            const formattedHeight =
              currentUnit === 'px'
                ? Math.round(heightInUnit)
                : heightInUnit.toFixed(2);
            $('#resize-height').val(formattedHeight);

            // Reset quality
            $('#resize-quality').val(currentQuality);
            $('#resize-quality-slider').val(currentQuality);

            // Update size info
            updateButtonState();
            updateQualityWarning();
          });

        // Initial button state check
        updateButtonState();
      };

      // Function to switch between preset and advanced mode
      switchToAdvanced = () => {
        Swal.update({
          html: getFormHTML(
            currentWidth,
            currentHeight,
            currentUnit,
            currentQuality
          ),
          showConfirmButton: true,
        });
        setupAdvancedHandlers();
      };

      switchToPresets = () => {
        Swal.update({
          html: getPresetHTML(),
          showConfirmButton: false,
        });
        setupPresetHandlers();
      };

      // Start with preset mode
      setupPresetHandlers();
    },
  });

  if (result.isConfirmed) {
    const finalWidth = Math.round(currentWidth);
    const finalHeight = Math.round(currentHeight);
    const finalQuality = currentQuality;

    await applyResizeImage(activeObject, finalWidth, finalHeight, finalQuality);
  }
}
